import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { sortBy, isEmpty } from 'lodash';
import { formatTimeShort } from '../../../utils/time';
import { selectors as arSchedulesSelectors } from '../../../state/modules/availability-requirement-schedules';
import { nextStatusChange } from '../../../state/modules/availability-requirement-schedules/utils';
import { selectors as availabilityRequirementSelectors } from '../../../state/modules/availability-requirements';
import { selectors as groupSelectors } from '../../../state/modules/groups';
import { selectors as skillSelectors } from '../../../state/modules/skills';
import SkillIcon from '../../general/SkillIcon';
import Loader from '../../general/Loader';
import ScheduleWarning from '../../schedule/ScheduleWarning';
import { Badge, Table } from '../../ui-kit';
import SettingsForm from '../widgets/ui-kit/SettingsForm';

const Settings = ({
  updateWidgetSettingsField,
  groupsById,
  skills,
  groupsTree,
  accessScopeGroupId,
  groupId,
  widgetSettings
}) => {
  const { selected_skill_ids, visible_availability_requirements } = widgetSettings;
  const currentGroup = groupsById[groupId];
  const visibleAvailabilityRequirements =
    visible_availability_requirements || currentGroup?.availability_requirements?.map(ar => ar.id) || [];

  const skillOptions = sortBy(skills, ['name']).map(s => {
    const content = (
      <React.Fragment>
        <SkillIcon skill={s} /> {s.name}
      </React.Fragment>
    );
    return { value: s.id, label: content };
  });

  const selectedSkillOptions = skillOptions.filter(s => selected_skill_ids.includes(s.value));

  const handleChange = events => {
    const selected_skill_ids = events.map(e => e.value);
    updateWidgetSettingsField('selected_skill_ids', selected_skill_ids);
  };

  const handleAvailabilityRequirementChange = selected =>
    updateWidgetSettingsField('visible_availability_requirements', selected);

  return (
    <SettingsForm
      withAutoScroll={true}
      withAutoResizeHeight={true}
      updateWidgetSettingsField={updateWidgetSettingsField}
      widgetSettings={widgetSettings}
    >
      <SettingsForm.Section>
        <SettingsForm.Section.Header />
        <SettingsForm.Section.Content>
          <SettingsForm.SkillSelector
            fieldName="selectedSkillOptions"
            selectedSkillOptions={selectedSkillOptions}
            handleChange={handleChange}
            skillOptions={skillOptions}
            label={I18n.t('activerecord.models.skill')}
          />
        </SettingsForm.Section.Content>
      </SettingsForm.Section>
      <SettingsForm.Section>
        <SettingsForm.Section.Header>{I18n.t('display')}</SettingsForm.Section.Header>
        <SettingsForm.Section.Content>
          <SettingsForm.AvailabilityRequirementsSelector
            groupsTree={groupsTree}
            parentGroupId={accessScopeGroupId || groupId}
            selectedAvailabilityRequirements={visibleAvailabilityRequirements}
            onChange={handleAvailabilityRequirementChange}
          />
        </SettingsForm.Section.Content>
      </SettingsForm.Section>
    </SettingsForm>
  );
};

const mapStateToPropsSettings = state => ({
  skills: skillSelectors.values(state),
  groupsById: groupSelectors.byId(state),
  groupsTree: groupSelectors.tree(state)
});
const ConnectedSettings = connect(mapStateToPropsSettings)(Settings);

const AvailabilityRequirementRow = ({ availabilityRequirement }) => {
  if (!availabilityRequirement) return null;
  return (
    <React.Fragment>
      <td>
        {!availabilityRequirement.currentStatus && <Loader />}
        <ScheduleWarning
          availabilityRequirement={availabilityRequirement}
          availabilityRequirementState={availabilityRequirement.currentStatus}
        />
      </td>
      <td>
        <Badge>{formatTimeShort(availabilityRequirement.nextChange.time)}</Badge>{' '}
        <ScheduleWarning
          inline
          availabilityRequirement={availabilityRequirement}
          availabilityRequirementState={availabilityRequirement.nextChange.status}
        />
      </td>
      {availabilityRequirement.usersBySkill.map((users, index) => (
        <td key={index}>
          {users.length > 0 &&
            users.map((user, index) => (
              <React.Fragment key={user.id}>
                {!!index && ', '}
                <a href={'/users/' + user.id}>
                  {user.first_name} {user.last_name}
                </a>
              </React.Fragment>
            ))}
          {users.length === 0 && <i>{I18n.t('nobody_available')}</i>}
        </td>
      ))}
      <td>
        <Badge pill={true}>{availabilityRequirement.personnelCount}</Badge>
      </td>
    </React.Fragment>
  );
};

const StationRows = ({ group, availabilityRequirements }) => (
  // The station name is rendered in a colspan. Then one row per
  // availabilityRequirement. Therefore, we need to render the first row
  // separate from the subsequent rows.
  <React.Fragment>
    <tr>
      <td rowSpan={availabilityRequirements.length}>
        <a href={'/groups/' + group.id}>
          {group.short_code} ({group.name})
        </a>
      </td>
      <AvailabilityRequirementRow availabilityRequirement={availabilityRequirements[0]} />
    </tr>
    {availabilityRequirements.slice(1).map(availabilityRequirement => (
      <tr key={availabilityRequirement.id}>
        <AvailabilityRequirementRow availabilityRequirement={availabilityRequirement} />
      </tr>
    ))}
  </React.Fragment>
);

class ApplianceList extends React.Component {
  constructor(props) {
    super(props);
    const lookAhead = 5 * 24 * 3600; // look ahead max 5 days for next change
    props.dashboardAPIManager.fetchSkills();
    props.dashboardAPIManager.fetchUsers({ params: { presentation_type: 'basic' } });
    props.dashboardAPIManager.fetchGroupAndSubgroups();
    props.dashboardAPIManager.fetchAvailabilityCodes();
    props.dashboardAPIManager.registerForAvailabilityRequirementScheduleUpdates({
      look_ahead: lookAhead
    });
  }

  get accessScopeGroupId() {
    return this.props.accessScopeGroupId || this.props.groupId;
  }

  static SettingsContent = ConnectedSettings;
  static defaultSettings = {
    selected_skill_ids: []
  };

  selectedSkillIds = () => this.props.selected_skill_ids || [];

  annotateAvailabilityRequirement = availabilityRequirement => {
    const now = moment(this.props.dashboardDatetime.dateTime);
    const schedule = this.props.arSchedules[availabilityRequirement.id];
    const intervals = (schedule && schedule.intervals) || [];
    const currentInterval = intervals.find(i => now.isBetween(moment(i.start_time), moment(i.end_time), null, '[)'));

    // The table will show a number of columns, for which we retrieve the
    // information here.
    // First, the current status of the AR
    const currentStatus = this.props.arCurrentState[availabilityRequirement.id];
    // Second, the next status change for this AR, if any
    const nextChange = nextStatusChange(schedule, null, this.props.availabilityCodes.byId, now);
    // Per skill that is selected in the settings, show the names of the users
    // who are currently on duty and have this skill
    const usersBySkill = this.selectedSkillIds().map(skillId =>
      ((currentInterval && currentInterval.available_memberships) || [])
        .filter(s => s.skill_ids.includes(skillId))
        .map(s => s.user_id)
        .map(s => this.props.users.byId[s])
        .filter(Boolean)
    );
    // Lastly, the total number of users who is currently on duty
    const personnelCount = currentInterval?.available_memberships?.length;

    return {
      ...availabilityRequirement,
      currentStatus,
      nextChange,
      usersBySkill,
      personnelCount
    };
  };

  isAvailabilityRequirementVisible = availabilityRequirementId => {
    if (!this.props.visible_availability_requirements) return true;

    return this.props.visible_availability_requirements.includes(availabilityRequirementId);
  };

  infoPerGroup = () => {
    const parentGroup = this.props.groups.byId[this.accessScopeGroupId];
    if (!parentGroup) return [];

    // create an array, and look up stations
    const entries = Object.entries(this.props.availabilityRequirementsPerGroup);
    const result = entries.map(([stationId, availabilityRequirements]) => {
      const group = this.props.groups.byId[stationId];
      if (!group || group.type !== 'station') return null;
      // group must be a subgroup of the chosen group, or the group itself. Note
      // that parentGroup can be a station.
      if (!parentGroup.descendant_ids.includes(group.id) && parentGroup.id !== group.id) return null;

      // Filter visible availability requirements
      availabilityRequirements = availabilityRequirements.filter(ar => this.isAvailabilityRequirementVisible(ar.id));

      if (isEmpty(availabilityRequirements)) return null;

      const annotatedAvailabilityRequirements = availabilityRequirements.map(availabilityRequirement =>
        this.annotateAvailabilityRequirement(availabilityRequirement)
      );

      return { group, availabilityRequirements: annotatedAvailabilityRequirements };
    });

    return sortBy(result.filter(Boolean), o => o.group.name);
  };

  selectedSkills = () =>
    this.selectedSkillIds()
      .map(s => this.props.skills.byId[s])
      .filter(Boolean);

  render() {
    return (
      <Table striped bordered condensed>
        <thead>
          <tr>
            <th>{I18n.t('activerecord.models.station.one')}</th>
            <th>{I18n.t('availability_requirements.current_status')}</th>
            <th>{I18n.t('availability_requirements.next_status_change')}</th>
            {this.selectedSkills().map(s => (
              <th key={s.id}>
                <SkillIcon skill={s} />
              </th>
            ))}
            <th>{I18n.t('currently_available')}</th>
          </tr>
        </thead>
        <tbody>
          {this.infoPerGroup().map(item => (
            <StationRows {...item} key={item.group.id} />
          ))}
        </tbody>
      </Table>
    );
  }
}

const mapStateToProps = state => ({
  availabilityRequirementsPerGroup: availabilityRequirementSelectors.availabilityRequirementsPerGroup(state),
  groups: state.groups,
  users: state.users,
  skills: state.skills,
  dashboardDatetime: state.dashboards.dashboardDatetime,
  arCurrentState: arSchedulesSelectors.selectDashboardState(state),
  arSchedules: state.availabilityRequirementSchedules.full,
  availabilityCodes: state.availabilityCodes
});

export default connect(mapStateToProps)(ApplianceList);
